import React, { useEffect, useState, createElement } from 'react';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { TeacherSidebar } from '../TeacherSidebar';
import { TrendingUpIcon, ClockIcon, DollarSignIcon, CheckCircleIcon, XCircleIcon, DownloadIcon, FileTextIcon, CreditCardIcon, MenuIcon, XIcon, LockIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import { subscribeLessons } from '../../src/services/lessonsService';
import type { Lesson } from '../../src/types/lesson';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
// Stat card component
const StatCard = ({
  icon,
  label,
  value,
  change,
  changeType,
  darkMode,
  helperText
}) => {
  return <div className={`p-6 rounded-xl ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-4710">
      <div className="flex justify-between items-start" data-id="element-4711">
        <div data-id="element-4712">
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-4713">
            {label}
          </p>
          <h3 className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-4714">
            {value}
          </h3>
          {helperText && <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-4715">
              {helperText}
            </p>}
        </div>
        <div className={`p-3 rounded-full ${darkMode ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-4716">
          {icon}
        </div>
      </div>
      {change && <div className="mt-4 flex items-center" data-id="element-4717">
          <span className={`text-sm ${changeType === 'increase' ? 'text-green-500' : 'text-red-500'}`} data-id="element-4718">
            {change}
          </span>
          <span className={`text-sm ml-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-4719">
            vs last month
          </span>
        </div>}
    </div>;
};
interface LessonEarning {
  id: string;
  date: string;
  student: string;
  instrument: string;
  duration: string;
  status: string;
}
export const RevenuePage = () => {
  const {
    theme
  } = useTheme();
  const {
    currentUser
  } = useAuth();
  const isDark = theme === 'dark';
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTable, setActiveTable] = useState<'earnings'>('earnings');
  // Real lesson data
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);
  // Filter states
  const [dateRange, setDateRange] = useState('This month');
  const [selectedStudent, setSelectedStudent] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  // Fetch real lesson data
  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    // Adjust date range based on filter
    let fromDate = startOfMonth;
    let toDate = endOfMonth;
    if (dateRange === 'Last month') {
      fromDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      toDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
    } else if (dateRange === 'Year to date') {
      fromDate = new Date(now.getFullYear(), 0, 1);
      toDate = endOfMonth;
    }
    const unsubscribe = subscribeLessons({
      teacherId: currentUser.uid,
      from: fromDate.toISOString(),
      to: toDate.toISOString(),
      onData: fetchedLessons => {
        setLessons(fetchedLessons);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [currentUser?.uid, dateRange]);
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  // Process lessons into earnings data (without fake $ amounts)
  const processLessonEarnings = (): LessonEarning[] => {
    return lessons.filter(lesson => {
      // Apply student filter only
      if (selectedStudent !== 'All' && lesson.studentName !== selectedStudent) return false;
      return true;
    }).map(lesson => {
      const startDate = new Date(lesson.startAt);
      const endDate = new Date(lesson.endAt);
      const durationMinutes = Math.round((endDate.getTime() - startDate.getTime()) / 60000);
      return {
        id: lesson.id,
        date: startDate.toLocaleDateString(),
        student: lesson.studentName || 'Unknown',
        instrument: lesson.instrument || 'N/A',
        duration: `${durationMinutes} min`,
        status: lesson.status === 'COMPLETED' ? 'Completed' : lesson.status === 'CANCELED' ? 'Cancelled' : 'Scheduled'
      };
    }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  };
  const lessonEarnings = processLessonEarnings();
  // Calculate KPIs from real data (no fake $ amounts)
  const calculateKPIs = () => {
    const completedLessons = lessons.filter(l => l.status === 'COMPLETED');
    const totalLessons = lessons.length;
    const totalMinutes = completedLessons.reduce((sum, lesson) => {
      const start = new Date(lesson.startAt);
      const end = new Date(lesson.endAt);
      return sum + Math.round((end.getTime() - start.getTime()) / 60000);
    }, 0);
    const totalHours = totalMinutes / 60;
    const completionRate = totalLessons > 0 ? completedLessons.length / totalLessons * 100 : 0;
    return {
      hoursTaught: totalHours.toFixed(1),
      completionRate: `${completionRate.toFixed(0)}%`
    };
  };
  const kpis = calculateKPIs();
  // Calculate hours by week for chart
  const calculateHoursByWeek = () => {
    const weekData: {
      [key: string]: number;
    } = {};
    lessons.filter(l => l.status === 'COMPLETED').forEach(lesson => {
      const startDate = new Date(lesson.startAt);
      const endDate = new Date(lesson.endAt);
      const durationHours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
      // Get week number
      const weekStart = new Date(startDate);
      weekStart.setDate(startDate.getDate() - startDate.getDay());
      const weekKey = `Week ${Math.ceil(startDate.getDate() / 7)}`;
      weekData[weekKey] = (weekData[weekKey] || 0) + durationHours;
    });
    return Object.entries(weekData).map(([week, hours]) => ({
      week,
      hours: Math.round(hours * 10) / 10
    }));
  };
  const hoursWeeklyData = calculateHoursByWeek();
  // Get unique students for filter
  const students = ['All', ...new Set(lessons.map(l => l.studentName).filter(Boolean))];
  // Export to CSV (without fake $ columns)
  const exportToCSV = () => {
    const headers = ['Date', 'Student', 'Instrument', 'Duration', 'Status'];
    const rows = lessonEarnings.map(e => [e.date, e.student, e.instrument, e.duration, e.status]);
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lesson-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };
  // Custom tooltip for charts
  const CustomTooltip = ({
    active,
    payload,
    label
  }) => {
    if (active && payload && payload.length) {
      return <div className={`p-3 rounded-lg shadow-md ${isDark ? 'bg-navy-700 text-white' : 'bg-white text-gray-800'}`} data-id="element-4720">
          <p className="font-medium" data-id="element-4721">{label}</p>
          <p style={{
          color: payload[0].color
        }} data-id="element-4722">
            {payload[0].name}: {payload[0].value}
          </p>
        </div>;
    }
    return null;
  };
  return <div className={`w-full min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white' : 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-800'} font-sans transition-colors duration-300`} data-id="element-4723">
      <Navbar data-id="element-4724" />
      <div className="pt-20 pb-16 flex flex-col lg:flex-row" data-id="element-4725">
        {/* Mobile sidebar toggle */}
        <button onClick={toggleSidebar} className="lg:hidden fixed bottom-4 right-4 z-30 bg-deep-pink text-white p-3 rounded-full shadow-lg flex items-center justify-center" aria-label={isSidebarOpen ? 'Close menu' : 'Open menu'} style={{
        minWidth: '56px',
        minHeight: '56px'
      }} data-id="element-4726">
          {isSidebarOpen ? <XIcon className="h-6 w-6" data-id="element-4727" /> : <MenuIcon className="h-6 w-6" data-id="element-4728" />}
        </button>

        {/* Left Sidebar */}
        <aside className={`w-64 fixed inset-y-0 pt-20 pb-16 ${isDark ? 'bg-navy-900/90 shadow-lg' : 'bg-white shadow-md'} z-20 transform transition-all duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`} data-id="element-4729">
          <TeacherSidebar darkMode={isDark} closeSidebar={() => setIsSidebarOpen(false)} data-id="element-4730" />
        </aside>

        {/* Main Content */}
        <main className={`flex-1 transition-all duration-300 ease-in-out ${isSidebarOpen ? 'lg:ml-64' : 'ml-0 lg:ml-64'} px-4 py-6`} data-id="element-4731">
          <div className="max-w-7xl mx-auto" data-id="element-4732">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6" data-id="element-4733">
              <h1 className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4734">
                Revenue
              </h1>
            </div>

            {/* Info Banner */}
            <div className={`p-4 rounded-xl ${isDark ? 'bg-navy-800 border border-navy-700' : 'bg-blue-50 border border-blue-200'} mb-6`} data-id="element-4735">
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-4736">
                Dollar amounts are unavailable until payment tracking is
                enabled.
              </p>
            </div>

            {/* Filters */}
            <div className={`p-4 rounded-xl ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm mb-6`} data-id="element-4737">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4738">
                <div data-id="element-4739">
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-4740">
                    Date Range
                  </label>
                  <select value={dateRange} onChange={e => setDateRange(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-800 focus:ring-deep-pink focus:border-deep-pink'} transition-colors`} data-id="element-4741">
                    <option data-id="element-4742">This month</option>
                    <option data-id="element-4743">Last month</option>
                    <option data-id="element-4744">Year to date</option>
                  </select>
                </div>
                <div data-id="element-4745">
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-4746">
                    Student
                  </label>
                  <select value={selectedStudent} onChange={e => setSelectedStudent(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-800 focus:ring-deep-pink focus:border-deep-pink'} transition-colors`} data-id="element-4747">
                    {students.map(student => <option key={student} data-id="element-4748">{student}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* KPI Cards - Only real metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6" data-id="element-4749">
              <StatCard icon={<ClockIcon className="h-6 w-6 text-blue-500" data-id="element-4751" />} label="Hours Taught" value={kpis.hoursTaught} darkMode={isDark} data-id="element-4750" />
              <StatCard icon={<CheckCircleIcon className="h-6 w-6 text-green-500" data-id="element-4753" />} label="Completion Rate" value={kpis.completionRate} darkMode={isDark} data-id="element-4752" />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-4754">
              {/* Revenue Over Time - Coming Soon */}
              <div className={`p-6 rounded-xl ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm relative`} data-id="element-4755">
                <div className="absolute inset-0 bg-black/5 backdrop-blur-sm rounded-xl flex items-center justify-center z-10" data-id="element-4756">
                  <div className="text-center" data-id="element-4757">
                    <LockIcon className={`h-12 w-12 mx-auto mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-4758" />
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4759">
                      Coming Soon
                    </p>
                    <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4760">
                      Requires Stripe connection
                    </p>
                  </div>
                </div>
                <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'} opacity-30`} data-id="element-4761">
                  Revenue Over Time
                </h3>
                <div className="h-[300px] opacity-30" data-id="element-4762"></div>
              </div>

              {/* Hours by Week - Real Data */}
              <div className={`p-6 rounded-xl ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-4763">
                <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4764">
                  Hours Taught by Week
                </h3>
                {hoursWeeklyData.length > 0 ? <ResponsiveContainer width="100%" height={300} data-id="element-4765">
                    <BarChart data={hoursWeeklyData} data-id="element-4766">
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} data-id="element-4767" />
                      <XAxis dataKey="week" stroke={isDark ? '#9ca3af' : '#6b7280'} data-id="element-4768" />
                      <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} data-id="element-4769" />
                      <Tooltip content={<CustomTooltip data-id="element-4771" />} data-id="element-4770" />
                      <Bar dataKey="hours" fill="#e63946" name="Hours" data-id="element-4772" />
                    </BarChart>
                  </ResponsiveContainer> : <div className="h-[300px] flex items-center justify-center" data-id="element-4773">
                    <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4774">
                      No completed lessons yet
                    </p>
                  </div>}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-4775">
              <button onClick={exportToCSV} disabled={lessonEarnings.length === 0} className={`flex items-center justify-center px-4 py-3 rounded-lg ${isDark ? 'bg-navy-800 hover:bg-navy-700' : 'bg-white hover:bg-gray-50'} shadow-sm transition-colors ${lessonEarnings.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-4776">
                <DownloadIcon className="h-5 w-5 mr-2 text-deep-pink" data-id="element-4777" />
                <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4778">
                  Export CSV
                </span>
              </button>
              <button disabled className={`flex items-center justify-center px-4 py-3 rounded-lg ${isDark ? 'bg-navy-800/50' : 'bg-gray-100'} shadow-sm opacity-50 cursor-not-allowed`} data-id="element-4779">
                <FileTextIcon className="h-5 w-5 mr-2 text-gray-500" data-id="element-4780" />
                <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-4781">
                  Download PDF
                </span>
              </button>
              <button disabled className={`flex items-center justify-center px-4 py-3 rounded-lg ${isDark ? 'bg-navy-800/50' : 'bg-gray-100'} shadow-sm opacity-50 cursor-not-allowed`} data-id="element-4782">
                <CreditCardIcon className="h-5 w-5 mr-2 text-gray-500" data-id="element-4783" />
                <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-4784">
                  Connect Stripe
                </span>
              </button>
              <button disabled className={`flex items-center justify-center px-4 py-3 rounded-lg ${isDark ? 'bg-navy-800/50' : 'bg-gray-100'} shadow-sm opacity-50 cursor-not-allowed`} data-id="element-4785">
                <FileTextIcon className="h-5 w-5 mr-2 text-gray-500" data-id="element-4786" />
                <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-4787">
                  Tax Documents
                </span>
              </button>
            </div>

            {/* Tables */}
            <div className={`p-6 rounded-xl ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-4788">
              {/* Table Tabs */}
              <div className="flex border-b border-gray-200 dark:border-navy-700 mb-6" data-id="element-4789">
                <button className={`px-4 py-2 font-medium text-deep-pink border-b-2 border-deep-pink`} data-id="element-4790">
                  Lesson History
                </button>
                <button disabled className={`px-4 py-2 font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'} opacity-50 cursor-not-allowed flex items-center gap-2`} data-id="element-4791">
                  Payouts
                  <LockIcon className="h-4 w-4" data-id="element-4792" />
                </button>
                <button disabled className={`px-4 py-2 font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'} opacity-50 cursor-not-allowed flex items-center gap-2`} data-id="element-4793">
                  Payments/Invoices
                  <LockIcon className="h-4 w-4" data-id="element-4794" />
                </button>
              </div>

              {/* Lesson History Table (no $ columns) */}
              <div className="overflow-x-auto" data-id="element-4795">
                {loading ? <div className="py-12 text-center" data-id="element-4796">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-deep-pink mx-auto" data-id="element-4797"></div>
                  </div> : lessonEarnings.length > 0 ? <table className="w-full" data-id="element-4798">
                    <thead data-id="element-4799">
                      <tr className={`border-b ${isDark ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-4800">
                        <th className={`text-left py-3 px-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-4801">
                          Date
                        </th>
                        <th className={`text-left py-3 px-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-4802">
                          Student
                        </th>
                        <th className={`text-left py-3 px-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-4803">
                          Instrument
                        </th>
                        <th className={`text-left py-3 px-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-4804">
                          Duration
                        </th>
                        <th className={`text-left py-3 px-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-4805">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-4806">
                      {lessonEarnings.map(lesson => <tr key={lesson.id} className={`border-b ${isDark ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-4807">
                          <td className={`py-3 px-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4808">
                            {lesson.date}
                          </td>
                          <td className={`py-3 px-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4809">
                            {lesson.student}
                          </td>
                          <td className={`py-3 px-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4810">
                            {lesson.instrument}
                          </td>
                          <td className={`py-3 px-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4811">
                            {lesson.duration}
                          </td>
                          <td className="py-3 px-4" data-id="element-4812">
                            <span className={`px-2 py-1 text-xs rounded-full ${lesson.status === 'Completed' ? isDark ? 'bg-green-900/30 text-green-200' : 'bg-green-100 text-green-800' : lesson.status === 'Cancelled' ? isDark ? 'bg-red-900/30 text-red-200' : 'bg-red-100 text-red-800' : isDark ? 'bg-blue-900/30 text-blue-200' : 'bg-blue-100 text-blue-800'}`} data-id="element-4813">
                              {lesson.status}
                            </span>
                          </td>
                        </tr>)}
                    </tbody>
                  </table> : <div className="py-12 text-center" data-id="element-4814">
                    <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4815">
                      No lessons found for the selected filters
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </main>

        {/* Overlay for mobile when sidebar is open */}
        {isSidebarOpen && <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-10" onClick={toggleSidebar} aria-hidden="true" data-id="element-4816"></div>}
      </div>
      <Footer data-id="element-4817" />
    </div>;
};